import type { Nullable } from 'types/common';

/**
 * Safely starts playback of a media element.
 *
 * `HTMLMediaElement.play()` returns a Promise that rejects asynchronously
 * (e.g. with an `AbortError`) when playback is interrupted by `pause()`, a new
 * `load()`/`src`, or the element being removed from the DOM. A synchronous
 * `try/catch` cannot catch that rejection, so it surfaces as an unhandled
 * rejection. We attach a `.catch()` to swallow these benign interruptions.
 *
 * The `undefined` guard covers legacy browsers where `play()` returns nothing.
 *
 * @see https://developer.chrome.com/blog/play-request-was-interrupted/
 */
export const safePlay = (video: Nullable<HTMLVideoElement>): Promise<void> => {
  if (!video) {
    return Promise.resolve();
  }

  const playPromise = video.play();

  if (playPromise === undefined) {
    return Promise.resolve();
  }

  return playPromise.catch(() => { /* Ignore play interruptions (AbortError, NotAllowedError, ...) */ });
};
